import React from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import NotificationSystem from '../components/NotificationSystem';
import SignatureHTMLRenderer from '../components/SignatureHTMLRenderer';
import { useNotification } from '../hooks/useNotification';
import {
  Email as EmailIcon,
  Sync as SyncIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

interface Signature {
  id: number;
  nome: string;
  dominio: string;
  html_gerado: string;
  variaveis: {
    name: string;
    title: string;
    mobile: string;
    email: string;
    photo_id: string;
  };
}

const MySignatures: React.FC = () => {
  const { user } = useAuth();
  const [signatures, setSignatures] = React.useState<Signature[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [syncing, setSyncing] = React.useState(false);
  
  // Estados para modais
  const [viewDialogOpen, setViewDialogOpen] = React.useState(false);
  const [selectedSignature, setSelectedSignature] = React.useState<Signature | null>(null);
  
  // Sistema de notificações padronizado
  const { notification, showSuccess, showError } = useNotification();

  // Função para carregar assinaturas da API
  const loadSignatures = async () => {
    try {
      setLoading(true);
      setError(null);
      
          const token = localStorage.getItem('authToken');
    const response = await fetch('/api/users/me/signatures', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
      
      if (!response.ok) {
        throw new Error('Erro ao carregar assinaturas');
      }
      
      const data = await response.json();
      setSignatures(data);
    } catch (err) {
      console.error('Erro ao carregar assinaturas:', err);
      setError('Erro ao carregar assinaturas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Carregar assinaturas quando o componente montar
  React.useEffect(() => {
    if (user) {
      loadSignatures();
    }
  }, [user]);

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/unified-signatures/sync-my-signatures', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao sincronizar assinaturas');
      }
      
      await response.json();
      
      // Mostrar notificação de sucesso
      showSuccess('Suas assinaturas foram sincronizadas com sucesso!');
      
      // Recarregar as assinaturas
      await loadSignatures();
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
      
      // Mostrar notificação de erro
      showError('Erro ao sincronizar assinaturas. Tente novamente.');
    } finally {
      setSyncing(false);
    }
  };

  const handleViewSignature = (signature: Signature) => {
    setSelectedSignature(signature);
    setViewDialogOpen(true);
  };

  const getPlatformIcon = (dominio: string) => {
    const icons = {
      'acspro.com.br': '📧',
      'accyberpro.com.br': '🖥️',
      'podcafe.tech': '☕',
      'acsflow.com.br': '🌊'
    };
    return icons[dominio as keyof typeof icons] || '📧';
  };

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh', 
      bgcolor: 'background.default', 
      py: 2
    }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Minhas Assinaturas
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Gerencie suas assinaturas de email em diferentes plataformas
        </Typography>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
          onClick={handleSyncAll}
          disabled={syncing}
        >
          {syncing ? 'Sincronizando...' : 'Sincronizar Minhas Assinaturas'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadSignatures}
          disabled={loading}
        >
          Atualizar
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {signatures.map((signature) => (
            <Grid item xs={12} sm={6} md={4} key={signature.id}>
              <Card 
                sx={{ 
                  height: 320, // Altura fixa para cards quadrados
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                {/* Header compacto */}
                <Box sx={{ 
                  p: 2, 
                  background: '#f8f9fa',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  minHeight: 60
                }}>
                  <Avatar sx={{ 
                    width: 28, 
                    height: 28, 
                    bgcolor: 'primary.main',
                    fontSize: '14px'
                  }}>
                    {getPlatformIcon(signature.dominio)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" sx={{ 
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#2c3e50'
                    }}>
                      {signature.nome}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {signature.variaveis.email}
                    </Typography>
                  </Box>
                </Box>

                {/* Conteúdo do Card */}
                <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Informações do Domínio */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                      Domínio: {signature.dominio}
                    </Typography>
                  </Box>

                  {/* Informações Pessoais */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                      Nome: {signature.variaveis.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Cargo: {signature.variaveis.title}
                    </Typography>
                  </Box>

                  {/* Informações de Contato */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                      Email: {signature.variaveis.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Telefone: {signature.variaveis.mobile || 'Não informado'}
                    </Typography>
                  </Box>

                  {/* Botão de Ação */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto' }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ViewIcon />}
                      onClick={() => handleViewSignature(signature)}
                      sx={{ 
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        }
                      }}
                    >
                      Visualizar
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && !error && signatures.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nenhuma assinatura encontrada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Suas assinaturas aparecerão aqui quando forem configuradas.
          </Typography>
        </Paper>
             )}

      {/* Modal de Visualização */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Visualizar Assinatura - {selectedSignature?.nome}
            </Typography>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSignature && (
            <Box>
              <Box sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 2, 
                mb: 2, 
                borderRadius: 1 
              }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Domínio:</strong> {selectedSignature.dominio}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Email:</strong> {selectedSignature.variaveis.email}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Nome:</strong> {selectedSignature.variaveis.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Cargo:</strong> {selectedSignature.variaveis.title}
                </Typography>
                <Typography variant="body2">
                  <strong>Telefone:</strong> {selectedSignature.variaveis.mobile || 'Não informado'}
                </Typography>
              </Box>
              
              <Box sx={{ 
                border: '1px solid #ccc', 
                p: 2, 
                borderRadius: 1,
                backgroundColor: 'white'
              }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Preview da Assinatura:</Typography>
                <SignatureHTMLRenderer 
                  html={selectedSignature.html_gerado}
                  style={{ 
                    minHeight: '450px',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Sistema de notificações padronizado */}
      <NotificationSystem
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={notification.onClose || (() => {})}
      />

    </Box>
  );
};

export default MySignatures;
